import { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import {
    User,
    Mail,
    Phone,
    Camera,
    Save,
    Loader2,
    Check,
    LogOut,
    Bell,
    Shield,
    ChevronRight,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ProfileSettings {
    full_name: string;
    email: string;
    phone: string;
    avatar_url: string;
}

export function AthleteProfilePage() {
    const { profile, signOut } = useAuth();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [saved, setSaved] = useState(false);

    const [profileForm, setProfileForm] = useState<ProfileSettings>({
        full_name: '',
        email: '',
        phone: '',
        avatar_url: '',
    });

    useEffect(() => {
        if (profile) {
            const p = profile as any;
            setProfileForm({
                full_name: p.full_name || '',
                email: p.email || '',
                phone: p.phone || '',
                avatar_url: p.avatar_url || '',
            });
        }
    }, [profile]);

    const saveProfileMutation = useMutation({
        mutationFn: async (data: ProfileSettings) => {
            const { error } = await supabase
                .from('users')
                .update({
                    full_name: data.full_name,
                    phone: data.phone,
                    avatar_url: data.avatar_url,
                })
                .eq('id', profile?.id);

            if (error) throw error;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['profile'] });
            setSaved(true);
            setTimeout(() => setSaved(false), 2000);
        },
    });

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        saveProfileMutation.mutate(profileForm);
    };

    const handleSignOut = async () => {
        await signOut();
        navigate('/login');
    };

    return (
        <div className="space-y-6 animate-fade-in max-w-2xl mx-auto">
            {/* Header */}
            <div className="text-center">
                <div className="w-24 h-24 mx-auto rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-3xl font-bold mb-4">
                    {profileForm.full_name?.charAt(0).toUpperCase() || 'A'}
                </div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                    Meu Perfil
                </h1>
                <p className="text-gray-500">Gerencie suas informações pessoais</p>
            </div>

            {/* Profile Form */}
            <form onSubmit={handleSubmit} className="card space-y-4">
                <div>
                    <label className="label">Nome Completo</label>
                    <div className="relative">
                        <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="text"
                            value={profileForm.full_name}
                            onChange={(e) => setProfileForm({ ...profileForm, full_name: e.target.value })}
                            className="input pl-10"
                            placeholder="Seu nome"
                        />
                    </div>
                </div>

                <div>
                    <label className="label">Email</label>
                    <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="email"
                            value={profileForm.email}
                            disabled
                            className="input pl-10 bg-gray-100 cursor-not-allowed"
                        />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">O email não pode ser alterado</p>
                </div>

                <div>
                    <label className="label">Telefone</label>
                    <div className="relative">
                        <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                        <input
                            type="tel"
                            value={profileForm.phone}
                            onChange={(e) => setProfileForm({ ...profileForm, phone: e.target.value })}
                            className="input pl-10"
                            placeholder="(00) 00000-0000"
                        />
                    </div>
                </div>

                <button
                    type="submit"
                    disabled={saveProfileMutation.isPending}
                    className="btn-primary w-full"
                >
                    {saveProfileMutation.isPending ? (
                        <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : saved ? (
                        <Check className="w-4 h-4 mr-2" />
                    ) : (
                        <Save className="w-4 h-4 mr-2" />
                    )}
                    {saved ? 'Salvo!' : 'Salvar Alterações'}
                </button>
            </form>

            {/* Quick Actions */}
            <div className="card divide-y divide-gray-100 dark:divide-gray-800">
                <button className="w-full flex items-center justify-between py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors -mx-4 px-4 first:rounded-t-xl">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                            <Bell className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                            <p className="font-medium text-gray-900 dark:text-white">Notificações</p>
                            <p className="text-sm text-gray-500">Gerencie suas preferências</p>
                        </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>

                <button className="w-full flex items-center justify-between py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors -mx-4 px-4">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                            <Shield className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                            <p className="font-medium text-gray-900 dark:text-white">Privacidade</p>
                            <p className="text-sm text-gray-500">Controle seus dados</p>
                        </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>

                <button
                    onClick={handleSignOut}
                    className="w-full flex items-center justify-between py-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors -mx-4 px-4 last:rounded-b-xl"
                >
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                            <LogOut className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                            <p className="font-medium text-red-600">Sair da Conta</p>
                            <p className="text-sm text-gray-500">Encerrar sessão</p>
                        </div>
                    </div>
                    <ChevronRight className="w-5 h-5 text-gray-400" />
                </button>
            </div>
        </div>
    );
}
